<template>
    <div>
        <document-title :value="documentTitle"></document-title>
        <loading v-if="loading"></loading>
        <div v-else>
            <div class="flex-con is-grow md-pad-lr is-stretch is-start primary-con">
                <div v-if="activeStep === STEP.ONE">
                    <div v-if="!cartItems.length">
                        <h2 class="title">
                            0 Items In Your Cart
                        </h2>
                        <router-link :to="{ name : $ROUTES.PRODUCTS_INDEX }">Find products to add</router-link>
                    </div>
                    <div v-else class="cart-products-con">
                        <cart-product
                            class="cart-product"
                            v-for="(context,i) in cartProducts"
                            :key="i"
                            :product="context.product"
                            :variant="context.variant"
                            @remove="removeItem(context)"
                        ></cart-product>
                    </div>

                    <div v-if="removedCartItems.length" class="removed-items">
                        <div
                            v-for="(context,i) in removedCartItems"
                            :key="i"
                            class="removed-item"
                        >
                            <div>You just removed : {{context.product.title}}</div>
                            <a @click="undo(context)">Undo</a>
                        </div>
                    </div>

                </div>
                <div v-else-if="activeStep === STEP.TWO" class="checkout-section-con">
                    <div class="checkout-section">
                        <h5 class="heading">Order Details</h5>
                        <div class="field">
                            <label class="label has-text-grey">Reference Number</label>
                            <input
                                v-model="data.referenceNumber"
                                class="input"
                                :class="{ 'is-danger' : errors.has('referenceNumber') }"
                                placeholder="Reference Number"
                                name="referenceNumber"
                                v-validate="'required'"
                            />
                        </div>
                        <div class="field">
                            <label class="label has-text-grey">Shipping Method</label>
                            <channel-shipping-method-selector
                                :channel-id="channelId"
                                v-model="data.channelShippingMethodId"
                                :is-required="true"
                            >
                            </channel-shipping-method-selector>
                        </div>
                    </div>
                    <div class="checkout-section">
                        <h5 class="heading">Shipping Address</h5>
                        <cart-address-selector
                            v-model="data.shippingAddress"
                            :saved-addresses="savedAddresses"
                            @delete-address="deleteAddress($event)"
                        />
                    </div>
                    <div class="checkout-section">
                        <h5 class="heading">Billing Address</h5>
                        <cart-address-selector
                            v-model="data.billingAddress"
                            :saved-addresses="savedAddresses"
                            is-billing
                            @delete-address="deleteAddress($event)"
                            :use-shipping-address="useShippingAddressForBilling"
                            @use-shipping="setBillingAsShipping($event)"
                        />
                    </div>
                    <div class="flex-con m-t-lg is-grow">
                        <div>
                            <div class="button is-small is-rounded" @click="activeStep = STEP.ONE">
                                <span class="icon">
                                    <i class="fa fa-angle-left"></i>
                                </span>
                                <span>
                                    Back
                                </span>
                            </div>
                        </div>
                        <div class="has-text-right">
                            <loading-button
                                :loading="loadingConfirm"
                                class="is-warning is-medium is-rounded is-uppercase"
                                @click="confirmOrder"
                            >
                                Confirm & Continue
                            </loading-button>
                        </div>
                    </div>
                </div>

                <div v-else-if="activeStep === STEP.THREE" class="checkout-section-con">
                    <div>
                        <h2 class="title m-b-n">Reference # {{data.referenceNumber}}</h2>
                        <div class="title m-v-md is-size-5">Shipping Method : {{channelShippingMethodName}}</div>
                        <div class="flex-con md-pad-lr m-t-lg is-start">
                            <div>
                                <h5 class="heading">Shipping Address</h5>
                                <address-display :address="data.shippingAddress.address"></address-display>
                            </div>
                            <div>
                                <h5 class="heading">Billing Address</h5>
                                <address-display :address="data.billingAddress.address"></address-display>
                            </div>
                        </div>
                    </div>
                    <div class="cart-products-con is-display-only">
                        <cart-product
                            class="cart-product"
                            v-for="(context,i) in cartProducts"
                            :key="i"
                            :product="context.product"
                            :variant="context.variant"
                            :display-only="true"
                            @remove="removeItem(context)"
                        ></cart-product>
                    </div>
                    <div class="flex-con m-t-lg is-grow">
                        <div>
                            <div class="button is-small is-rounded" @click="activeStep = STEP.TWO">
                                <span class="icon">
                                    <i class="fa fa-angle-left"></i>
                                </span>
                                <span>
                                    Back
                                </span>
                            </div>
                        </div>
                        <div class="has-text-right">
                            <loading-button
                                :loading="loadingPlaceOrder"
                                class="is-warning is-uppercase is-rounded is-medium"
                                @click="placeOrder"
                            >
                                Place Order
                            </loading-button>
                        </div>
                    </div>
                </div>

                <div class="cart-con">
                    <h2 class="heading is-size-5 cart-heading">Your Cart</h2>
                    <table class="table no-side-pad is-fullwidth">
                        <tbody>
                            <tr>
                                <th scope="row">SubTotal ({{cartData.totalQuantity}} items)</th>
                                <td>
                                    <money :value="cartData.subTotal"></money>
                                </td>
                            </tr>
                            <tr>
                                <th scope="row">Shipping Cost</th>
                                <td>
                                    <money v-if="sampleInvoice" :value="sampleInvoice.shippingCost" or="$0.00"></money>
                                    <small v-else>Aval In Checkout</small>
                                </td>
                            </tr>
                            <tr>
                                <th scope="row">Dropship Fee</th>
                                <td>
                                    <money v-if="sampleInvoice" :value="sampleInvoice.dropshipFee" or="$0.00"></money>
                                    <small v-else>Aval In Checkout</small>
                                </td>
                            </tr>
                        </tbody>
                    </table>

                    <div class="total-con flex-con is-grow sm-pad-lr">
                        <div>
                            <b>Total</b>
                        </div>
                        <div class="has-text-right">
                            <span v-if="activeStep !== STEP.THREE">~</span>
                            <money :value="cartData.totalCost"></money>
                        </div>
                    </div>

                    <button
                        v-if="activeStep === STEP.ONE"
                        class="button is-fullwidth is-warning is-uppercase is-rounded"
                        :disabled="!cartItems.length"
                        @click="activeStep = STEP.TWO"
                    >
                        Checkout
                    </button>
                    <loading-button
                        v-else-if="activeStep === STEP.THREE"
                        :loading="loadingPlaceOrder"
                        class="is-fullwidth is-warning is-uppercase is-rounded"
                        @click="placeOrder"
                    >
                        Place Order
                    </loading-button>
                </div>
            </div>


        </div>
    </div>
</template>

<script>
import ProductsApi from '../../api/Products';
import ChannelApi from '../../api/Channels';
import _ from 'underscore';
import {uniq} from 'underscore';
import CartProduct from "./components/CartProduct";
import OrdersApi from '../../api/Orders';
import AddressDisplay from "./components/AddressDisplay";
import ChannelShippingMethodSelector from "../../components/orders/components/ChannelShippingMethodSelector";
import CartAddressSelector from "./components/CartAddressSelector";
import AccountSettingsMixin from "../../mixins/AccountSettingsMixin";
export default {
    name : "CartIndex",
    components : {
        CartAddressSelector,
        ChannelShippingMethodSelector,
        AddressDisplay,
        CartProduct
    },
    mixins : [AccountSettingsMixin],
    data () {
        return {
            STEP : {
                ONE : 1,
                TWO : 2,
                THREE : 3,
            },
            activeStep : 1,
            loading : true,
            allProducts : [],
            removedCartItems : [],
            loadingConfirm : false,
            loadingPlaceOrder : false,
            data : {
                referenceNumber : null,
                channelShippingMethodId : 0,
                shippingAddress : {
                    save : false,
                    address : { id : null },
                    setDefault : false,
                },
                billingAddress : {
                    save : false,
                    address : { id : null },
                    setDefault : false,
                },
            },
            useShippingAddressForBilling : false,
            sampleInvoice : null,
            methods : null,
            savedAddresses : null,
        };
    },
    beforeMount () {

        if(!this.showCheckout) {
            this.$router.replace({ name : this.$ROUTES.DASHBOARD });
            return;
        }

        const ids = uniq(this.cartItems.map(i => i.productId));
        if(!ids.length) {
            this.loading = false;
            return;
        }
        Promise.all([
            ProductsApi.getProducts(ids).then((response) => {
                this.allProducts = response.data;
                //check that all the items that were stored in the cart are still available
                //and remove any that no longer are
                this.cartProducts.forEach(item => {
                    if(!item.product || !item.variant) {
                        this.$store.commit(this.$STORE_CONSTANTS.CART.REMOVE_ITEM, item.cartItem.variantId);
                    }
                });
            }),
            this.getChannelShippingMethods(),
            this.getSavedAddresses()
        ]).finally(() => {
            this.loading = false;
        })

        ;
    },
    computed : {
        documentTitle () {
            return this.loading ? 'Cart' : `Cart (${this.cartData.totalQuantity} items)`;
        },
        channelId(){
            return this.$getDealer().channelId;
        },
        cartItems () {
            return this.$store.state[this.$STORE_CONSTANTS.CART].items;
        },
        channelShippingMethodName(){
            if(!this.methods || !this.data.channelShippingMethodId){
                return '';
            }
            let method = _.findWhere(this.methods, { id : this.data.channelShippingMethodId });
            if(!method){
                return '';
            }
            return method.name;
        },
        cartProducts () {
            return this.cartItems.map(item => {
                const product = this.allProducts.find(p => p.id === item.productId);
                return {
                    cartItem : item,
                    product : product,
                    variant : product ? product.variants.find(v => v.id === item.variantId) : null,
                };
            });
        },
        cartData () {
            const data = {
                subTotal : 0,
                totalCost : 0,
                totalQuantity : 0,
            };
            this.cartProducts.forEach(context => {
                if(!context.product || !context.variant) {
                    return;
                }
                data.subTotal += context.variant.price * context.cartItem.quantity;
                data.totalQuantity += context.cartItem.quantity;
            });
            data.totalCost = data.subTotal;
            if(this.sampleInvoice) {
                data.totalCost += this.sampleInvoice.shippingCost;
                data.totalCost += this.sampleInvoice.dropshipFee;
            }
            return data;
        },
    },
    methods : {
        getSavedAddresses () {
            return OrdersApi.getSavedAddresses().then(response => {
                this.savedAddresses = response.data;
            });
        },
        getChannelShippingMethods(){
            return ChannelApi.getShippingMethods(this.channelId).then((response) => {
                this.methods = response.data;
            });
        },
        removeItem (context) {
            this.$store.commit(this.$STORE_CONSTANTS.CART.REMOVE_ITEM, context.variant.id);
            this.removedCartItems.push(context);
        },
        undo(context) {
            this.removedCartItems = this.removedCartItems.filter(c => c.variant.id !== context.variant.id);
            this.$store.commit(this.$STORE_CONSTANTS.CART.ADD_ITEM, context.cartItem);
        },
        getRequestContext () {
            const data = {
                ...this.data,
                items : this.cartItems,
            };
            if(this.useShippingAddressForBilling) {
                data.billingAddress.address = {...data.shippingAddress.address, id : null };
            }
            return data;
        },
        confirmOrder () {
            this.$validator.validateAll().then(valid => {
                if(!valid){
                    this.$notify({
                        text : 'Please fix the errors listed in red and try again',
                        type : 'error',
                    });
                    return;
                }
                this.loadingConfirm = true;
                OrdersApi.sampleInvoice(this.getRequestContext()).then(response => {
                    this.sampleInvoice = response.data;
                    this.activeStep = this.STEP.THREE;
                    this.loadingConfirm = false;
                });
            });
        },
        placeOrder () {
            this.loadingPlaceOrder = true;
            OrdersApi.create(this.getRequestContext()).then(response => {
                this.$store.commit(this.$STORE_CONSTANTS.CART.REMOVE_ALL);
                this.$router.push({ name : this.$ROUTES.ORDERS_VIEW, params : { id : response.data.id }});
            }).finally(() => {
                this.loadingPlaceOrder = false;
            });
        },

        deleteAddress (address) {
            this.savedAddresses = this.savedAddresses.filter(a => a.id !== address.id);
            OrdersApi.deleteAddress(address.id);
        },

        setBillingAsShipping (value) {
            this.useShippingAddressForBilling = value;
        },

    },
}
</script>

<style lang="scss" scoped>
    @import '../../assets/css/_theme';

    .primary-con {
        margin-bottom:40px;
    }

    .cart-products-con {
        &.is-display-only {
            margin-top:40px;
            padding-top:40px;
        }
        & > .cart-product {
            &:not(:last-child) {
                margin-bottom:40px;
                padding-bottom:40px;
                border-bottom:1px solid $grey-lighter;
            }
        }
    }

    .removed-items {
        margin-top:60px;
        padding-top:60px;
        border-top:1px solid $grey-lighter;
        & > .removed-item {
            border-left:2px solid $warning;
            background-color:$white-bis;
            padding:10px;
            &:not(:last-child) {
                margin-bottom:20px;
            }

            div {
                margin-bottom:10px;
            }

            a {
                padding-bottom:1px;
                border-bottom:1px solid $link;
            }
        }
    }

    .cart-con {
        flex:0 0 360px !important;
        border-left:1px solid $grey-lighter;

        .cart-heading {
            margin-bottom:10px;
        }

        table {
            margin-bottom:0;
            th {
                color:$grey;
                font-weight:100;
            }
            tr > td {
                &:last-child {
                    text-align:right;
                }
            }
        }

        .total-con {
            margin:40px 0;
            font-size:20px;
            span {
                font-size:21px;
                font-weight:bold;
            }
        }
    }

    .checkout-section-con {
        & > .checkout-section {
            margin: 20px;
            padding: 20px 0;
            border-top: 1px solid $grey-lighter;
            &:first-child {
                border-top:none;
                margin-top:0;
                padding-top:0;
            }

            .heading {
                font-size: 20px;
                margin-bottom: 20px;
            }
        }
    }

</style>
